import {
  type LogOverviewServerFilters,
  type LoggingOverviewDateRange,
  fetchLogOverview,
  fetchLogOverviewFilterValues,
} from '@/api/hooks/useIntegrationLogs'
import { useTenantConfig } from '@/api/hooks/useTenantConfig'
import {
  clearRowCountCache,
  useServerSideDataSource,
} from '@/api/useServerSideDatasource/useServerSideDatasource'
import { useDebouncedSearch } from '@/pages/Dashboard/panels/ReferenceDataPanel/hooks/useDebouncedSearch'
import { useTenantStore } from '@/stores/tenantStore'
import { ALL_MODES, type EnvModeSelection, toModeParam } from '@/utils/envMode'
import { useLocalStorage } from '@gravitate-js/excalibrr'
import type { GridApi } from 'ag-grid-community'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { defaultLogsRange } from '../LogsRangePicker'
import { LogsGrid } from './components/LogsGrid'
import {
  EMPTY_LOG_FILTERS,
  type OwnerOption,
  toOrderLogsFilter,
} from './components/logsFilterModel'

export function ConnectionLogsPanel() {
  const gridApiRef = useRef<GridApi | null>(null)
  const [params] = useSearchParams()
  const orderNumberParam = params.get('order')

  // Evaluated once at mount, so the picker opens on a real range instead of a
  // blank end date and the window doesn't creep while the page sits open.
  const [dateRange, setDateRange] = useState<
    LoggingOverviewDateRange | undefined
  >(defaultLogsRange)
  // Test/Prod tabs. Defaults to Prod so the panel opens on real traffic; the
  // backend resolves the mode against this tenant's own instances. A deep link
  // opens on All instead -- it names one order, and which instance that order
  // ran against isn't something the person following the link knows.
  const [mode, setMode] = useState<EnvModeSelection>(
    orderNumberParam ? ALL_MODES : 'Prod',
  )
  const [logFilters, setLogFilters] = useState(EMPTY_LOG_FILTERS)
  const logFilterQuery = useMemo(
    () => toOrderLogsFilter(logFilters),
    [logFilters],
  )

  const selectedTenantName = useTenantStore().selectedTenant?.name

  const { value: showExceptionsOnly, setValue: setShowExceptionsOnly } =
    useLocalStorage('integration-logs::filters::show-exceptions', false)

  // On by default: an order with every checkpoint silenced has been dealt
  // with deliberately, so it's noise until someone goes looking for it.
  const { value: hideSilenced, setValue: setHideSilenced } = useLocalStorage(
    'integration-logs::filters::hide-silenced',
    true,
  )

  // The ?order= deep link rides in on the search box rather than an equals
  // filter on the Order column: search matches either side's order number,
  // while the column holds only whichever one is ours on this routing.
  const { searchTerm, setSearchTerm, inputValue } = useDebouncedSearch(
    500,
    orderNumberParam ?? '',
  )

  // Memoized: an unstable filters object rebuilds the datasource on every
  // render, which resets the grid to the first page.
  const serverFilters = useMemo<LogOverviewServerFilters>(
    () => ({
      tenantName: selectedTenantName,
      type: 'order',
      dateRange,
      filter: logFilterQuery,
      mode: toModeParam(mode),
      hideSilenced: Boolean(hideSilenced),
      issuesOnly: Boolean(showExceptionsOnly),
    }),
    [
      selectedTenantName,
      dateRange,
      logFilterQuery,
      mode,
      hideSilenced,
      showExceptionsOnly,
    ],
  )

  const { serverSideDatasource, isFetching, rowCount } =
    useServerSideDataSource(
      selectedTenantName ? serverFilters : null,
      searchTerm,
      fetchLogOverview,
    )

  const getFilterOptions = useCallback(
    (column: string, filterModel: Record<string, unknown>) =>
      fetchLogOverviewFilterValues(
        column,
        serverFilters,
        filterModel,
        searchTerm,
      ),
    [serverFilters, searchTerm],
  )

  const refreshLogs = useCallback(() => {
    clearRowCountCache()
    gridApiRef.current?.refreshServerSide({ purge: true })
  }, [])

  const { data: tenantConfig } = useTenantConfig()
  const ownerOptions = useMemo<OwnerOption[]>(() => {
    if (!tenantConfig) return []
    // Partners are per instance pair, so one partner tenant can repeat.
    const byId = new Map<string, string>([
      [tenantConfig.tenant_id, tenantConfig.tenant_name],
      ...tenantConfig.partners.map(
        (partner) => [partner.tenant_id, partner.tenant_name] as const,
      ),
    ])
    return [...byId].map(([value, label]) => ({ value, label }))
  }, [tenantConfig])

  // Deep links from the Crossroads page. The ?order= one is already seeded
  // into the search box above; this covers the sessionStorage route, which
  // only lands after mount.
  useEffect(() => {
    if (orderNumberParam) {
      setSearchTerm(orderNumberParam)
      setMode(ALL_MODES)
      return
    }
    const navigationData = sessionStorage.getItem(
      'integrationManagementNavigation',
    )
    if (!navigationData) return
    try {
      const payload = JSON.parse(navigationData)
      if (payload.fromCrossroads && payload.recordId) {
        setSearchTerm(payload.recordId)
      }
    } catch (error) {
      console.error('Failed to apply navigation filters:', error)
    } finally {
      sessionStorage.removeItem('integrationManagementNavigation')
    }
  }, [orderNumberParam, setSearchTerm])

  return (
    <LogsGrid
      mode={mode}
      onModeChange={setMode}
      gridApiRef={gridApiRef}
      serverSideDatasource={serverSideDatasource}
      rowCount={rowCount}
      isLoading={isFetching}
      getFilterOptions={getFilterOptions}
      onRefresh={refreshLogs}
      showExceptionsOnly={showExceptionsOnly}
      setShowExceptionsOnly={setShowExceptionsOnly}
      hideSilenced={hideSilenced}
      setHideSilenced={setHideSilenced}
      onSearchChange={setSearchTerm}
      searchValue={inputValue}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      logFilters={logFilters}
      onLogFiltersChange={setLogFilters}
      ownerOptions={ownerOptions}
    />
  )
}
